import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

// Critical path components - loaded immediately
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import FirstLaunchDisclaimer from "./components/FirstLaunchDisclaimer";

// Route-level code splitting for better initial load
// These pages are lazy-loaded when the user navigates to them
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ "./pages/Dashboard"));
const StockListsPage = lazy(() => import(/* webpackChunkName: "stock-lists-page" */ "./pages/StockListsPage"));
const AdminDashboard = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/AdminDashboard"));
const UserProfile = lazy(() => import(/* webpackChunkName: "profile" */ "./pages/UserProfile"));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "legal" */ "./pages/PrivacyPolicy"));
const Disclaimer = lazy(() => import(/* webpackChunkName: "legal" */ "./pages/Disclaimer"));
const DataPolicy = lazy(() => import(/* webpackChunkName: "legal" */ "./pages/DataPolicy"));
const Support = lazy(() => import(/* webpackChunkName: "support" */ "./pages/Support"));
const About = lazy(() => import(/* webpackChunkName: "about" */ "./pages/About"));
const SharedPicks = lazy(() => import(/* webpackChunkName: "shared-picks" */ "./pages/SharedPicks"));

// Global loading fallback for route transitions
const PageLoader = () => (
  <div 
    className="min-h-screen bg-[#09090b] flex items-center justify-center"
    role="status"
    aria-label="Loading page"
  >
    <div className="text-center">
      <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto mb-4" />
      <p className="text-zinc-400">Loading...</p>
    </div>
  </div>
);

function AppRouter() {
  return (
    <>
      <FirstLaunchDisclaimer />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/data-policy" element={<DataPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/about" element={<About />} />
          <Route path="/share/:shareId" element={<SharedPicks />} />
          
          {/* Protected routes */}
          <Route path="/stock-lists" element={<ProtectedRoute><StockListsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          
          {/* Redirects */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppRouter />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#18181b',
            color: '#fff',
            border: '1px solid #27272a',
          },
        }}
      />
    </Router>
  );
}

export default App;
